import React from 'react';
import { timeAxis } from './timeAxis.js';
import { useChartTooltip } from './useChartTooltip.jsx';

// One Brier score per set, positioned by the date its predictions were written — see timeAxis.js
// for how the axis handles six annual sets and sixty weekly ones with the same code.
//
// The base-rate line is the chart's point. A Brier of 0.18 means nothing alone — it means "better
// than 0.21", the score you'd have got that year by ignoring every question and answering the
// year's own hit rate to all of them. Below the dashes is where forecasting added information.

// Same box and padding as CalibrationChart and DistributionChart. The charts share a grid, so
// matching the viewBox is what keeps 11px tick text 11px in all four rather than each chart's type
// scaling by its own column width.
const WIDTH = 420;
const HEIGHT = 320;
const PAD = { top: 16, right: 18, bottom: 42, left: 46 };
const PLOT_W = WIDTH - PAD.left - PAD.right;
const PLOT_H = HEIGHT - PAD.top - PAD.bottom;

// Brier lives in [0, 1] but a set of real forecasts lives in [0, 0.3]. Scaling to the data rather
// than the theoretical range is what makes a 0.04 improvement visible at all.
function domain(points) {
  const values = points.flatMap(p => [p.brier, p.baseline]).filter(v => v !== null && v !== undefined);
  const max = values.length ? Math.max(...values) : 0.25;
  return Math.max(0.1, Math.ceil((max + 0.02) * 20) / 20);
}

export default function BrierOverTimeChart({ points }) {
  const tip = useChartTooltip();
  if (!points || points.length < 2) return null;

  const yMax = domain(points);
  const axis = timeAxis(points, PAD.left, PLOT_W);
  const yScale = v => PAD.top + (1 - v / yMax) * PLOT_H;

  // Every step lands on an exact two-decimal value, so the axis never prints 0.03 for 0.025.
  const step = yMax > 0.2 ? 0.05 : yMax > 0.1 ? 0.02 : 0.01;
  const ticks = [];
  for (let t = 0; t <= yMax + 1e-9; t += step) ticks.push(Math.round(t * 1000) / 1000);

  const line = key =>
    points
      .map((p, i) => ({ v: p[key], x: axis.x[i] }))
      .filter(p => p.v !== null && p.v !== undefined)
      .map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)},${yScale(p.v).toFixed(1)}`)
      .join(' ');

  const last = points[points.length - 1];

  return (
    <figure className="pred-chart" ref={tip.ref}>
      <figcaption className="pred-chart-title">Brier score over time</figcaption>
      <p className="pred-legend">
        <span className="pred-legend-item">
          <span className="pred-legend-swatch is-primary" aria-hidden="true" />
          Brier score
        </span>
        <span className="pred-legend-item">
          <span className="pred-legend-swatch is-reference" aria-hidden="true" />
          Base-rate baseline
        </span>
        <span className="pred-legend-note">lower is better</span>
      </p>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="pred-chart-svg"
        role="img"
        aria-label="Brier score for each set, by the date it was written, against the base-rate baseline"
      >
        {ticks.map(t => (
          <g key={t}>
            <line
              className="pred-chart-grid"
              x1={PAD.left}
              x2={WIDTH - PAD.right}
              y1={yScale(t)}
              y2={yScale(t)}
            />
            <text className="pred-chart-tick" x={PAD.left - 8} y={yScale(t) + 4} textAnchor="end">
              {t.toFixed(2)}
            </text>
          </g>
        ))}

        <path className="pred-chart-reference" d={line('baseline')} />
        <path className="pred-chart-line" d={line('brier')} />

        {points.map((p, i) => (
          <g key={p.key}>
            {axis.radius > 0 && p.baseline !== null && p.baseline !== undefined && (
              <circle
                className="pred-chart-point is-reference"
                cx={axis.x[i]}
                cy={yScale(p.baseline)}
                r={Math.max(2, axis.radius - 1.5)}
              />
            )}
            {axis.radius > 0 && p.brier !== null && p.brier !== undefined && (
              <circle className="pred-chart-point" cx={axis.x[i]} cy={yScale(p.brier)} r={axis.radius} />
            )}
            {axis.showLabel[i] && (
              <text className="pred-chart-tick" x={axis.x[i]} y={HEIGHT - PAD.bottom + 18} textAnchor="middle">
                {p.label}
              </text>
            )}
            {/* The hover band, not the mark: the marks can end up 3px apart. The axis only has room
                for a short label, so everything else about the set lives in the tooltip. */}
            <rect
              x={axis.bands[i].x}
              y={PAD.top}
              width={axis.bands[i].width}
              height={PLOT_H}
              fill="transparent"
              {...tip.bind({
                title: p.fullLabel,
                rows: [
                  ['Brier', p.brier === null ? 'not graded' : p.brier.toFixed(3)],
                  ['Baseline', p.baseline === null || p.baseline === undefined ? null : p.baseline.toFixed(3)],
                  ['Graded', p.scored],
                  ['Written', p.writtenOn],
                  ['Evaluated', p.evaluatedOn]
                ]
              })}
            />
          </g>
        ))}

        {/* Only the endpoint is labelled — the axis and the tooltips carry the rest. */}
        {last.brier !== null && last.brier !== undefined && (
          <text
            className="pred-chart-value"
            x={axis.x[points.length - 1]}
            y={yScale(last.brier) - 12}
            textAnchor="end"
          >
            {last.brier.toFixed(3)}
          </text>
        )}

        <text className="pred-chart-axis" x={PAD.left + PLOT_W / 2} y={HEIGHT - 6} textAnchor="middle">
          {axis.dated ? 'Date written' : 'Set, written oldest first'}
        </text>
        <text
          className="pred-chart-axis"
          transform={`translate(12 ${PAD.top + PLOT_H / 2}) rotate(-90)`}
          textAnchor="middle"
        >
          Brier score
        </text>
      </svg>
      {tip.node}
      <p className="pred-chart-note">
        Below the dashed line is a set whose forecasts beat simply guessing its own hit rate every
        time. Ungraded sets are left out.
      </p>
    </figure>
  );
}
